import { type ReactNode, useId } from 'react'
import type { Observable } from 'rxjs'
import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListEmptyState,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Button } from '@/components/Button'
import { formatPhysicalAddress } from '@/utils/address-util'
import type { PhysicalAddress } from '@/features/tax-profile/PhysicalAddress'
import type { IconName } from '@/components/Icon'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import { useIsSpacious } from '@/hooks/viewport-hooks'

/**
 * Props for {@link TaxRefundMethodCardListSection}.
 */
export interface TaxRefundMethodCardListSectionProps {
  /**
   * The section title.
   */
  title: string

  /**
   * The children to render (the list items).
   */
  children?: ReactNode

  /**
   * Optional action for the section header.
   */
  action?: ReactNode

  /**
   * Render the empty state.
   */
  renderEmptyState?: () => ReactNode

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A section within the tax refund method list.
 */
export function TaxRefundMethodCardListSection({
  title,
  children,
  action,
  isSkeleton,
  renderEmptyState,
  testId,
}: TaxRefundMethodCardListSectionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const headlineId = useId()
  return (
    <section className="pb-lg" data-test={testId}>
      <SectionHeadline isSkeleton={isSkeleton} id={headlineId} action={action}>
        {title}
      </SectionHeadline>
      <CardList
        aria-labelledby={headlineId}
        renderEmptyState={renderEmptyState}
      >
        {children}
      </CardList>
    </section>
  )
}

/**
 * Tax Refund Method Card List Section Skeleton
 */
export function TaxRefundMethodCardListSectionSkeleton() {
  return (
    <TaxRefundMethodCardListSection isSkeleton title="Placdeholder">
      <TaxRefundMethodCardListItem
        title="Placeholder"
        subtitle="Placeholder"
        isSkeleton
        icon="card"
      />
    </TaxRefundMethodCardListSection>
  )
}

/**
 * Props for {@link ProvidedTaxRefundMethodCardListEmptyState}.
 */
export interface ProvidedTaxRefundMethodCardListEmptyStateProps {
  /**
   * Whether the user has existing methods.
   */
  hasExistingMethods?: boolean

  /**
   * Action to show in the empty state.
   */
  action?: ReactNode
}

/**
 * Empty state for the tax refund method list.
 */
export function ProvidedTaxRefundMethodCardListEmptyState({
  action,
  hasExistingMethods,
}: ProvidedTaxRefundMethodCardListEmptyStateProps) {
  return (
    <CardListEmptyState
      action={action}
      title={
        hasExistingMethods
          ? 'Use one of your existing tax refund methods, or add a new one.'
          : 'Where should the IRS send your refund?'
      }
    />
  )
}

/**
 * Props for {@link TaxRefundMethodCardListItem}.
 */
export interface TaxRefundMethodCardListItemProps {
  /**
   * The title of the method.
   */
  title: string

  /**
   * The subtitle of the method.
   */
  subtitle: string

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Whether the method is the current one.
   */
  isCurrent?: boolean

  /**
   * Icon to use.
   */
  icon?: IconName
}

/**
 * Individual tax refund method card item.
 */
export function TaxRefundMethodCardListItem({
  title,
  subtitle,
  onAction,
  action,
  isDisabled,
  isSkeleton,
  isCurrent,
  icon = 'card',
}: TaxRefundMethodCardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const textValue = `${title} - ${subtitle}`
  const isSpacious = useIsSpacious()

  return (
    <CardListItem
      textValue={isSkeleton ? textValue : 'Placeholder'}
      onAction={isSkeleton ? undefined : onAction}
      isDisabled={isDisabled || isSkeleton}
      isActive={isCurrent}
    >
      <CardItemContent
        isSkeleton={isSkeleton}
        isInteractive={Boolean(onAction && !action)}
        icon={isSpacious && <CardItemIcon skeleton={isSkeleton} name={icon} />}
        extras={action}
      >
        <CardTitle>
          <AutoSkeleton isSkeleton={isSkeleton}>{title}</AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="sm-description">
          <AutoSkeleton isSkeleton={isSkeleton}>{subtitle}</AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * Props for {@link OtherMethodDropdown}.
 */
export interface OtherMethodDropdownProps {
  /**
   * Callback to select the method.
   */
  onSelect: () => unknown

  /**
   * Callback to delete the method.
   */
  onDelete: () => unknown

  /**
   * Whether actions are disabled.
   */
  isDisabled?: boolean

  /**
   * Observable to trigger opening the dropdown.
   */
  open$?: Observable<unknown>
}

/**
 * Dropdown for other refund methods.
 */
export function OtherMethodDropdown({
  onSelect,
  onDelete,
  isDisabled,
  open$,
}: OtherMethodDropdownProps) {
  return (
    <Dropdown
      title="More options"
      open$={open$}
      trigger={
        <IconButton
          sm
          variant="no-hover"
          name="more"
          label="Actions"
          isDisabled={isDisabled}
        />
      }
    >
      <DropdownItem icon="tick" onAction={onSelect}>
        Use this method
      </DropdownItem>
      <DropdownItem icon="delete" onAction={onDelete}>
        Delete
      </DropdownItem>
    </Dropdown>
  )
}

/**
 * Props for {@link AddNewRefundMethodButton}.
 */
export interface AddNewRefundMethodButtonProps {
  /**
   * Callback when the button is pressed.
   */
  onPress: () => unknown
  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean
}

/**
 * Button to add a new refund method.
 */
export function AddNewRefundMethodButton({
  onPress,
  isDisabled,
}: AddNewRefundMethodButtonProps) {
  return (
    <Button
      label="New method"
      variant="text"
      onPress={onPress}
      iconLeft="add"
      isDisabled={isDisabled}
    />
  )
}

/**
 * Props for {@link DirectDepositMethodCardListItem}.
 */
export interface DirectDepositMethodCardListItemProps {
  /**
   * The bank account number.
   */
  accountNumber: string

  /**
   * The type of account.
   */
  accountType: 'checking' | 'savings'

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the method is the current one.
   */
  isCurrent?: boolean
}

/**
 * Direct deposit method card.
 */
export function DirectDepositMethodCardListItem({
  accountNumber,
  accountType,
  onAction,
  action,
  isDisabled,
  isCurrent,
}: DirectDepositMethodCardListItemProps) {
  const lastFour = accountNumber
    .slice(-4)
    .padStart(Math.min(accountNumber.length, 4), accountNumber)
  const accountTypeLabel = accountType === 'checking' ? 'Checking' : 'Savings'

  return (
    <TaxRefundMethodCardListItem
      isCurrent={isCurrent}
      title="Direct deposit"
      icon="legend-business"
      subtitle={`${accountTypeLabel} account ending in ${lastFour}`}
      onAction={onAction}
      action={action}
      isDisabled={isDisabled}
    />
  )
}

/**
 * Props for {@link CheckMethodCardListItem}.
 */
export interface CheckMethodCardListItemProps {
  /**
   * Street address line 1.
   */
  addressLine1: string | null

  /**
   * Street address line 2.
   */
  addressLine2?: string | null

  /**
   * City.
   */
  city: string | null

  /**
   * State/region.
   */
  region: string | null

  /**
   * Postal/ZIP code.
   */
  postalCode: string | null

  /**
   * Country.
   */
  country: string | null

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the method is the current one.
   */
  isCurrent?: boolean
}

/**
 * Paper check method card.
 */
export function CheckMethodCardListItem({
  addressLine1,
  addressLine2,
  city,
  region,
  postalCode,
  country,
  onAction,
  action,
  isDisabled,
  isCurrent,
}: CheckMethodCardListItemProps) {
  const address: Partial<PhysicalAddress> = {
    addressLine1: addressLine1 || '',
    addressLine2: addressLine2 || '',
    city: city || '',
    region: region || '',
    postalCode: postalCode || '',
    country: country || '',
  }
  const formattedAddress = formatPhysicalAddress(address)
  const subtitle = formattedAddress || 'Same as contact info'

  return (
    <TaxRefundMethodCardListItem
      title="Paper check"
      icon="nav-home"
      subtitle={subtitle}
      onAction={onAction}
      action={action}
      isDisabled={isDisabled}
      isCurrent={isCurrent}
    />
  )
}

/**
 * Props for {@link FutureTaxCreditMethodCardListItem}.
 */
export interface FutureTaxCreditMethodCardListItemProps {
  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the method is the current one.
   */
  isCurrent?: boolean
}

/**
 * Future tax credit method card.
 */
export function FutureTaxCreditMethodCardListItem({
  onAction,
  action,
  isDisabled,
  isCurrent,
}: FutureTaxCreditMethodCardListItemProps) {
  return (
    <TaxRefundMethodCardListItem
      title="Future tax credit"
      subtitle="Applied to next year's taxes"
      icon="calendar"
      onAction={onAction}
      action={action}
      isDisabled={isDisabled}
      isCurrent={isCurrent}
    />
  )
}
